import { BuildOutlined } from '@ant-design/icons'
import { Collapse, Spin } from 'antd'
import { AddButton } from 'components/CantileverButton'
import { ErrorScreen } from 'components/ErrorScreen'
import { PageHeader } from 'components/PageHeader'
import { AssetOverviewAdminGate } from 'features/assetDatabase/components/gates/AssetOverviewGates'
import { useLandingPageData } from 'features/partsDatabase/hooks/useLandingPageData'
import {
  PartsData,
  PartsTableEntries,
  UnitAssetModelTable,
  UnitInspections,
} from 'features/partsDatabase/types/partsTableTypes'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { useMemo } from 'react'
import { GeckoSite } from 'types'

import { PartsTable } from './PartsTable'
import {
  assetInspections,
  createPartsTableData,
  getAssetModels,
} from './PartsTableHelpers'

const { Panel } = Collapse

interface PartsTableDataProps {
  site: GeckoSite | undefined
  siteId: string
}

const PartsTableData: React.FC<PartsTableDataProps> = (props) => {
  const { siteId } = props
  const { setDrawerState, setDrawerEntry, setOnDrawerClose } = useRightDrawer()

  const { isLoading: isLoadingPage } = useLandingPageData({ siteID: siteId })
  const {
    data: assetData,
    isLoading: isLoadingData,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<PartsTableEntries[]>({
    type: [
      'unit',
      'asset-model',
      'component',
      'inspection',
      'component-defaults',
    ],
    tags: { site: siteId },
    order_by: [{ key: 'last_updated', descending: 'true' }],
    limit: 1000000,
  })

  const unitAssets = assetData
    ?.filter((asset) => asset.entry.type === 'unit')
    .map((unit) => unit.entry)

  const unitComponents = assetData
    ?.filter((asset) => asset.entry.type === 'component')
    .map((unit) => unit.entry)

  const unitAssetModels: UnitAssetModelTable[] | undefined =
    getAssetModels(assetData)

  const unitInspections: UnitInspections[] | undefined =
    assetInspections(assetData)

  const partsData: PartsData[] = useMemo(() => {
    return unitAssets && unitAssetModels && unitComponents && unitInspections
      ? createPartsTableData(unitAssets, unitAssetModels, unitInspections)
      : []
  }, [unitAssets, unitAssetModels, unitComponents, unitInspections])

  const errorScreenMessage =
    'Please contact support for the proper permissions.'

  return (
    <>
      <PageHeader
        title='Parts'
        icon={<BuildOutlined />}
        trailingContent={
          <AssetOverviewAdminGate>
            <AddButton
              entityName='Asset'
              onClick={() => {
                if (siteId) {
                  setDrawerEntry(props.site)
                  setDrawerState(DrawerState.EditGeckoUnit)
                  setOnDrawerClose(() => mutate)
                }
              }}
            />
          </AssetOverviewAdminGate>
        }></PageHeader>
      <>
        {isLoadingData || isLoadingPage ? (
          <Spin spinning={true} />
        ) : !siteId ? (
          <ErrorScreen
            title={'Site was not properly loaded'}
            message={errorScreenMessage}
          />
        ) : partsData && partsData.length > 0 ? (
          <Collapse accordion defaultActiveKey={partsData[0].id}>
            {partsData.map((part) => (
              <Panel header={part.name} key={part.id}>
                <PartsTable
                  siteId={siteId}
                  isLoading={isLoadingData}
                  data={[part]}
                />
              </Panel>
            ))}
          </Collapse>
        ) : (
          <ErrorScreen
            title={'No Assets for the Site'}
            message={errorScreenMessage}
          />
        )}
      </>
    </>
  )
}

export default PartsTableData
